import React, { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  enableShare,
  disableShare,
  uploadClientLogo,
  deleteClientLogo,
  emailReport,
  API_BASE,
} from "@/lib/api";
import { toast } from "sonner";
import {
  ShareNetwork,
  Copy,
  Eye,
  EyeSlash,
  UploadSimple,
  EnvelopeSimple,
  PaperPlaneTilt,
  Trash,
  Image as ImageIcon,
} from "@phosphor-icons/react";

export default function ShareButton({ website }) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");

  const token = website?.share_token;
  const shareUrl = token ? `${window.location.origin}/r/${token}` : "";
  const clientLogoUrl = website?.client_logo_path
    ? `${API_BASE}/public/logos/${website.client_logo_path}`
    : null;

  const enableMut = useMutation({
    mutationFn: () => enableShare(website.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["website", website.id] });
      qc.invalidateQueries({ queryKey: ["websites"] });
      toast.success("Public report link created");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to enable sharing"),
  });

  const disableMut = useMutation({
    mutationFn: () => disableShare(website.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["website", website.id] });
      qc.invalidateQueries({ queryKey: ["websites"] });
      toast.success("Public report link disabled");
    },
  });

  const uploadMut = useMutation({
    mutationFn: (file) => uploadClientLogo(website.id, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["website", website.id] });
      qc.invalidateQueries({ queryKey: ["websites"] });
      toast.success("Client logo uploaded");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Upload failed"),
  });

  const deleteLogoMut = useMutation({
    mutationFn: () => deleteClientLogo(website.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["website", website.id] });
      qc.invalidateQueries({ queryKey: ["websites"] });
      toast.success("Logo removed");
    },
  });

  const emailMut = useMutation({
    mutationFn: () =>
      emailReport(website.id, {
        recipient_email: emailTo,
        subject: emailSubject || undefined,
        message: emailMessage || undefined,
      }),
    onSuccess: () => {
      toast.success(`Report emailed to ${emailTo}`);
      setEmailTo("");
      setEmailMessage("");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Email failed"),
  });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  const onFileChosen = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      toast.error("File too large (max 2 MB)");
      return;
    }
    uploadMut.mutate(f);
    e.target.value = "";
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="share-btn"
          className="rounded-none bg-white border border-[var(--rt-border)] text-[var(--rt-text)] hover:bg-[var(--rt-surface-hover)] h-10 px-4 flex items-center gap-2"
        >
          <ShareNetwork size={14} weight={token ? "fill" : "regular"} />
          {token ? "Share link" : "Share"}
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border border-[var(--rt-border)] bg-white max-w-2xl">
        <DialogHeader>
          <div className="label-eyebrow mb-2">/ client report</div>
          <DialogTitle className="font-display text-2xl">Share &amp; brand</DialogTitle>
          <DialogDescription className="text-sm text-[var(--rt-text-muted)]">
            Send a polished, read-only report for <span className="font-mono-data">{website?.domain}</span>.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="link" className="mt-2">
          <TabsList className="rounded-none bg-[var(--rt-surface-hover)] p-0 h-10 grid grid-cols-3 w-full">
            <TabsTrigger value="link" data-testid="tab-link" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[var(--rt-primary)] h-full">
              Public link
            </TabsTrigger>
            <TabsTrigger value="logo" data-testid="tab-logo" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[var(--rt-primary)] h-full">
              Client logo
            </TabsTrigger>
            <TabsTrigger value="email" data-testid="tab-email" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[var(--rt-primary)] h-full">
              Email
            </TabsTrigger>
          </TabsList>

          {/* PUBLIC LINK TAB */}
          <TabsContent value="link" className="mt-5">
            {token ? (
              <div className="space-y-4">
                <div>
                  <div className="label-eyebrow mb-2">Public URL</div>
                  <div className="flex items-center gap-2">
                    <Input value={shareUrl} readOnly data-testid="share-url-input" className="rounded-none border-[var(--rt-border)] font-mono-data text-xs" />
                    <Button data-testid="copy-share-url" onClick={handleCopy} className="rounded-none bg-[var(--rt-primary)] hover:bg-[var(--rt-primary-hover)] text-white h-9 px-3 flex items-center gap-2">
                      <Copy size={14} /> Copy
                    </Button>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-[var(--rt-border)]">
                  <a href={shareUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-[var(--rt-primary)] hover:underline" data-testid="open-share-url">
                    <Eye size={14} /> Preview report
                  </a>
                  <Button data-testid="disable-share-btn" onClick={() => disableMut.mutate()} disabled={disableMut.isPending} variant="ghost" className="rounded-none text-[var(--rt-danger)] hover:bg-red-50 hover:text-[var(--rt-danger)] flex items-center gap-2">
                    <EyeSlash size={14} /> Disable link
                  </Button>
                </div>
              </div>
            ) : (
              <Button data-testid="enable-share-btn" onClick={() => enableMut.mutate()} disabled={enableMut.isPending} className="rounded-none bg-[var(--rt-primary)] hover:bg-[var(--rt-primary-hover)] text-white h-10 px-4 w-full flex items-center justify-center gap-2">
                <ShareNetwork size={14} />
                {enableMut.isPending ? "Generating…" : "Generate public link"}
              </Button>
            )}
          </TabsContent>

          {/* CLIENT LOGO TAB */}
          <TabsContent value="logo" className="mt-5 space-y-4">
            <p className="text-sm text-[var(--rt-text-muted)] leading-relaxed">
              Upload your client&apos;s own logo to display alongside Geokath&apos;s branding on their report.
              PNG / JPG / WEBP / SVG, max 2&nbsp;MB.
            </p>
            <div className="border border-dashed border-[var(--rt-border)] p-6 flex items-center gap-5">
              <div className="w-24 h-24 bg-[#FAFAFA] border border-[var(--rt-border)] flex items-center justify-center shrink-0">
                {clientLogoUrl ? (
                  <img src={clientLogoUrl} alt="Client logo" className="max-w-full max-h-full object-contain" data-testid="client-logo-preview" />
                ) : (
                  <ImageIcon size={28} className="text-[var(--rt-text-muted)]" />
                )}
              </div>
              <div className="flex-1">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                  className="hidden"
                  onChange={onFileChosen}
                  data-testid="logo-file-input"
                />
                <Button
                  data-testid="upload-logo-btn"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploadMut.isPending}
                  className="rounded-none bg-[var(--rt-primary)] hover:bg-[var(--rt-primary-hover)] text-white h-10 px-4 flex items-center gap-2"
                >
                  <UploadSimple size={14} weight="bold" />
                  {uploadMut.isPending ? "Uploading…" : clientLogoUrl ? "Replace logo" : "Upload logo"}
                </Button>
                {clientLogoUrl && (
                  <Button
                    data-testid="remove-logo-btn"
                    onClick={() => deleteLogoMut.mutate()}
                    variant="ghost"
                    className="rounded-none text-[var(--rt-danger)] hover:bg-red-50 hover:text-[var(--rt-danger)] ml-2 h-10"
                  >
                    <Trash size={14} className="mr-1" /> Remove
                  </Button>
                )}
              </div>
            </div>
          </TabsContent>

          {/* EMAIL TAB */}
          <TabsContent value="email" className="mt-5 space-y-4">
            {!token ? (
              <div className="border border-[var(--rt-warning)] bg-yellow-50 p-4 text-sm">
                <strong>Enable the public link first.</strong> Emails include a link to the live report,
                so the public link must be active before sending.
              </div>
            ) : (
              <>
                <div>
                  <Label className="label-eyebrow">Recipient email</Label>
                  <Input
                    data-testid="email-to-input"
                    type="email"
                    value={emailTo}
                    onChange={(e) => setEmailTo(e.target.value)}
                    placeholder="client@example.com"
                    className="rounded-none border-[var(--rt-border)] mt-1.5"
                  />
                </div>
                <div>
                  <Label className="label-eyebrow">Subject (optional)</Label>
                  <Input
                    data-testid="email-subject-input"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder={`SEO Rank Report — ${website?.name || website?.domain || ""}`}
                    className="rounded-none border-[var(--rt-border)] mt-1.5"
                  />
                </div>
                <div>
                  <Label className="label-eyebrow">Personal message (optional)</Label>
                  <Textarea
                    data-testid="email-message-input"
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e.target.value)}
                    rows={4}
                    placeholder="Hi Sarah, here is your weekly rank tracking report. Please reach out if you have any questions!"
                    className="rounded-none border-[var(--rt-border)] mt-1.5 text-sm"
                  />
                </div>
                <Button
                  data-testid="send-email-btn"
                  onClick={() => emailMut.mutate()}
                  disabled={!emailTo || emailMut.isPending}
                  className="rounded-none bg-[var(--rt-primary)] hover:bg-[var(--rt-primary-hover)] text-white h-10 px-4 w-full flex items-center justify-center gap-2"
                >
                  <PaperPlaneTilt size={14} weight="fill" />
                  {emailMut.isPending ? "Sending…" : "Send report"}
                </Button>
                <p className="text-xs text-[var(--rt-text-muted)] text-center">
                  Email includes KPI summary + link to the live interactive report.
                </p>
              </>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
